import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { EICFConsentsApi } from '../../../apis/EICFApi/EICFConsentsApi';
import { QueryKeys } from '../../../queryDefaults/QueryKeys';
import { AppMedError } from '../../../error/AppMedError';
import { buildPdfFilename } from './useDownloadEICFConsentPdf';

export interface IDownloadMySignedEICFConsentPdfParams {
    language: string;
    patientName?: string | null;
    patientDateOfBirth?: string | null;
    templateName?: string | null;
}

/**
 * Patient-facing download of their own signed consent. The consent is resolved
 * server-side from the logged patient (no id is sent), so a patient can only
 * ever download their own consent.
 */
export function useDownloadMySignedEICFConsentPdf() {
    const { t } = useTranslation();

    return useMutation<
        { blob: Blob; filename: string | null },
        AppMedError,
        IDownloadMySignedEICFConsentPdfParams
    >({
        mutationKey: [QueryKeys.eicfMySignedConsent, 'downloadPdf'],
        mutationFn: async ({ language }) => {
            return await EICFConsentsApi.downloadMySignedPdf(language);
        },
        onSuccess: ({ blob, filename }, params) => {
            EICFConsentsApi.downloadPdfFile(
                blob,
                filename || buildPdfFilename(params),
            );

            notifications.show({
                title: t('eicf.notifications.downloadPdfSuccess'),
                message: t('eicf.notifications.downloadPdfSuccessMessage'),
                color: 'green',
            });
        },
        onError: (error: AppMedError) => {
            notifications.show({
                title: t('eicf.notifications.downloadPdfError'),
                message:
                    error?.message ||
                    t('eicf.notifications.downloadPdfErrorMessage'),
                color: 'red',
            });
        },
    });
}
